/*
HTML escaping is the same as React's
(on purpose.) Therefore, it has the following Copyright and Licensing:

Copyright 2013-2014, Facebook, Inc.
All rights reserved.

This source code is licensed under the BSD-style license found in the LICENSE
file in the root directory of React's source tree.
*/
var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArrays = this && this.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
  for (var r = Array(s), k = 0, i = 0; i < il; i++) for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) r[k] = a[j];
  return r;
};
import * as React from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import * as memoize from 'fast-memoize';
import { invariant } from '@formatjs/ecma402-abstract';
import { UnsupportedFormatterError } from './error';
export function filterProps(props, whitelist, defaults) {
  if (defaults === void 0) {
    defaults = {};
  }
  return whitelist.reduce(function (filtered, name) {
    if (name in props) {
      filtered[name] = props[name];
    } else if (name in defaults) {
      filtered[name] = defaults[name];
    }
    return filtered;
  }, {});
}
export function invariantIntlContext(intl) {
  invariant(intl, '[React Intl] Could not find required `intl` object. ' + '<IntlProvider> needs to exist in the component ancestry.');
}
export var defaultErrorHandler = function defaultErrorHandler(error) {
  if (process.env.NODE_ENV !== 'production') {
    console.error(error);
  }
};
export var DEFAULT_INTL_CONFIG = {
  formats: {},
  messages: {},
  timeZone: undefined,
  textComponent: React.Fragment,
  defaultLocale: 'en',
  defaultFormats: {},
  onError: defaultErrorHandler
};
export function createIntlCache() {
  return {
    dateTime: {},
    number: {},
    message: {},
    relativeTime: {},
    pluralRules: {},
    list: {},
    displayNames: {}
  };
}
function createFastMemoizeCache(store) {
  return {
    create: function create() {
      return {
        has: function has(key) {
          return key in store;
        },
        get: function get(key) {
          return store[key];
        },
        set: function set(key, value) {
          store[key] = value;
        }
      };
    }
  };
}
// @ts-ignore this is to deal with rollup's default import shenanigans
var _memoizeIntl = memoize.default || memoize;
var memoizeIntl = _memoizeIntl;
/**
 * Create intl formatters and populate cache
 * @param cache explicit cache to prevent leaking memory
 */
export function createFormatters(cache) {
  if (cache === void 0) {
    cache = createIntlCache();
  }
  var RelativeTimeFormat = Intl.RelativeTimeFormat;
  var ListFormat = Intl.ListFormat;
  var DisplayNames = Intl.DisplayNames;
  var getDateTimeFormat = memoizeIntl(function () {
    var _a;
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    return new ((_a = Intl.DateTimeFormat).bind.apply(_a, __spreadArrays([void 0], args)))();
  }, {
    cache: createFastMemoizeCache(cache.dateTime),
    strategy: memoizeIntl.strategies.variadic
  });
  var getNumberFormat = memoizeIntl(function () {
    var _a;
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    return new ((_a = Intl.NumberFormat).bind.apply(_a, __spreadArrays([void 0], args)))();
  }, {
    cache: createFastMemoizeCache(cache.number),
    strategy: memoizeIntl.strategies.variadic
  });
  var getPluralRules = memoizeIntl(function () {
    var _a;
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }
    return new ((_a = Intl.PluralRules).bind.apply(_a, __spreadArrays([void 0], args)))();
  }, {
    cache: createFastMemoizeCache(cache.pluralRules),
    strategy: memoizeIntl.strategies.variadic
  });
  return {
    getDateTimeFormat: getDateTimeFormat,
    getNumberFormat: getNumberFormat,
    getMessageFormat: memoizeIntl(function (message, locales, overrideFormats, opts) {
      return new IntlMessageFormat(message, locales, overrideFormats, __assign({
        formatters: {
          getNumberFormat: getNumberFormat,
          getDateTimeFormat: getDateTimeFormat,
          getPluralRules: getPluralRules
        }
      }, opts || {}));
    }, {
      cache: createFastMemoizeCache(cache.message),
      strategy: memoizeIntl.strategies.variadic
    }),
    getRelativeTimeFormat: memoizeIntl(function () {
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      return new (RelativeTimeFormat.bind.apply(RelativeTimeFormat, __spreadArrays([void 0], args)))();
    }, {
      cache: createFastMemoizeCache(cache.relativeTime),
      strategy: memoizeIntl.strategies.variadic
    }),
    getPluralRules: getPluralRules,
    getListFormat: memoizeIntl(function () {
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      return new (ListFormat.bind.apply(ListFormat, __spreadArrays([void 0], args)))();
    }, {
      cache: createFastMemoizeCache(cache.list),
      strategy: memoizeIntl.strategies.variadic
    }),
    getDisplayNames: memoizeIntl(function () {
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      return new (DisplayNames.bind.apply(DisplayNames, __spreadArrays([void 0], args)))();
    }, {
      cache: createFastMemoizeCache(cache.displayNames),
      strategy: memoizeIntl.strategies.variadic
    })
  };
}
export function getNamedFormat(formats, type, name, onError) {
  var formatType = formats && formats[type];
  var format;
  if (formatType) {
    format = formatType[name];
  }
  if (format) {
    return format;
  }
  onError(new UnsupportedFormatterError("No " + type + " format named: " + name));
}
/**
 * Takes a `formatXMLElementFn`, and composes it in function, which passes
 * argument `parts` through, assigning unique key to each part, to prevent
 * "Each child in a list should have a unique "key"" React error.
 * @param formatXMLElementFn
 */
export function assignUniqueKeysToParts(formatXMLElementFn) {
  return function (parts) {
    // eslint-disable-next-line prefer-rest-params
    return formatXMLElementFn(React.Children.toArray(parts));
  };
}